// src/net/boardClient.ts — the CLIENT's board API. Pure `fetch`; no Anthropic SDK; NO key.
//
// fetchBoard(topicRaw) POSTs {VITE_API_BASE_URL}/board and, on ANY failure (network / 422 / 429 /
// 503 / timeout ~6s / malformed body), FALLS BACK to a matching seed board if one exists (matched by
// a normalized key), else surfaces a friendly BoardError. This is the "instant seed fallback" that
// keeps the game playable when the live generator is unavailable. getDaily() mirrors the same
// resilience for the Daily button.
//
// The backend returns a board that already matches Core BoardContent (it maps is_correct->kind and
// enforces counts/char-limits), so a 200 body needs only a light structural sanity check before we
// hand it to the Sim.
import type { BoardContent } from '../core/types';
import { validateBoardContent } from '../data/boardSchema';
import { SEED_BOARDS, getSeedBoard } from '../data/seedPack';
import {
  BoardError,
  type BoardErrorReason,
  type DailyResponse,
  type FetchBoardOptions,
  type FetchBoardResult,
} from './types';

const DEFAULT_TIMEOUT_MS = 6_000;

/** Base URL of the Render Web Service. Empty -> relative (same-origin) requests. Trailing / trimmed. */
function apiBase(): string {
  // Guard import.meta.env: Vite always defines it in the browser bundle, but be defensive so a
  // non-Vite runtime (tests, SSR probes) can't crash the call before it reaches fetch().
  const raw = import.meta.env?.VITE_API_BASE_URL ?? '';
  return raw.replace(/\/+$/, '');
}

/**
 * Normalize a free-typed topic into a comparable key. Seed keys look like
 * "geography::countries_europe"; a typed "Countries in Europe!" should still be able to find it.
 * We lowercase, strip punctuation, drop a few stopwords, and collapse whitespace to single _.
 */
const STOPWORDS = new Set(['the', 'a', 'an', 'of', 'in', 'our', 'is', 'are', 'and', 'to', 'that']);

export function normalizeTopicKey(topicRaw: string): string {
  return topicRaw
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 0 && !STOPWORDS.has(w))
    .join('_');
}

/** Tokens (sorted, stopword-free) of a topic — used for loose seed matching. */
function tokenSet(topicRaw: string): Set<string> {
  return new Set(normalizeTopicKey(topicRaw).split('_').filter(Boolean));
}

/**
 * Best-effort match of a typed topic to a seed board for the offline/error fallback path.
 * Order of attempts, most specific first:
 *   1. exact seed slug ("geography::countries_europe")
 *   2. the slug's tail after "::" equals the normalized key ("countries_europe")
 *   3. strong token overlap with a seed's raw label or rule (>=2 shared meaningful tokens, or a
 *      single token that fully covers the typed topic — e.g. "mammals" -> the Mammals seed)
 * Returns undefined if nothing is a confident match (caller then surfaces a friendly message).
 */
export function findSeedFallback(topicRaw: string): BoardContent | undefined {
  const key = normalizeTopicKey(topicRaw);
  if (!key) return undefined;

  // 1. exact slug
  const bySlug = getSeedBoard(topicRaw.trim());
  if (bySlug) return bySlug;

  const wanted = tokenSet(topicRaw);

  let best: { board: BoardContent; score: number } | undefined;
  for (const board of SEED_BOARDS) {
    const tail = board.topicNormalized.includes('::')
      ? board.topicNormalized.slice(board.topicNormalized.indexOf('::') + 2)
      : board.topicNormalized;
    // 2. normalized key matches the slug tail exactly
    if (tail === key) return board;

    // 3. token overlap against the seed's raw label + rule statement
    const seedTokens = tokenSet(`${board.topicRaw} ${board.ruleStatement}`);
    let shared = 0;
    for (const w of wanted) if (seedTokens.has(w)) shared++;
    // A single typed word that the seed fully owns (e.g. "mammals") is a confident match.
    const coversTyped = wanted.size > 0 && shared === wanted.size;
    const score = shared + (coversTyped ? 1 : 0);
    if (score >= 2 && (!best || score > best.score)) best = { board, score };
  }
  return best?.board;
}

/** A short, player-facing line for each failure mode (no jargon, never a stack). */
function friendlyFor(reason: BoardErrorReason): string {
  switch (reason) {
    case 'rate_limited':
      return "Whoa, slow down! You're cooking boards faster than the oven. Try again in a moment.";
    case 'unprocessable':
      return "Couldn't make a fair board from that. Try a more factual topic (e.g. “Planets”, “European countries”).";
    case 'unavailable':
      return 'The board kitchen is closed right now. Pick a quick-pick topic to keep playing.';
    case 'timeout':
      return 'That took too long to cook. Try again, or pick a quick-pick topic.';
    case 'offline':
      return "Can't reach the kitchen — you might be offline. Quick-pick topics still work.";
    case 'bad_response':
      return "The board came out wrong. Try again or pick a quick-pick topic.";
    case 'generating':
      return 'Your verified board is still cooking — give it a few seconds, then tap BUILD again.';
    default:
      return 'Something went wrong. Pick a quick-pick topic to keep playing.';
  }
}

/** Map an HTTP status (or absence) to a coarse reason. */
function reasonForStatus(status: number): BoardErrorReason {
  if (status === 429) return 'rate_limited';
  if (status === 422) return 'unprocessable';
  if (status === 503 || (status >= 500 && status < 600)) return 'unavailable';
  // 4xx other than 429/422: the topic itself is the problem -> treat like unprocessable.
  if (status >= 400 && status < 500) return 'unprocessable';
  return 'bad_response';
}

/** POST a JSON body with a hard timeout, honoring an external abort signal too. */
async function postJson(
  url: string,
  body: unknown,
  opts: FetchBoardOptions,
): Promise<Response> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const ctrl = new AbortController();
  const onAbort = (): void => ctrl.abort();
  if (opts.signal) {
    if (opts.signal.aborted) ctrl.abort();
    else opts.signal.addEventListener('abort', onAbort, { once: true });
  }
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
  } finally {
    clearTimeout(timer);
    if (opts.signal) opts.signal.removeEventListener('abort', onAbort);
  }
}

async function getJson(url: string, opts: FetchBoardOptions): Promise<Response> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const ctrl = new AbortController();
  const onAbort = (): void => ctrl.abort();
  if (opts.signal) {
    if (opts.signal.aborted) ctrl.abort();
    else opts.signal.addEventListener('abort', onAbort, { once: true });
  }
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(url, { method: 'GET', headers: { accept: 'application/json' }, signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
    if (opts.signal) opts.signal.removeEventListener('abort', onAbort);
  }
}

/** True if an abort came from the *caller's* signal (a real cancel) vs. our timeout. */
function isCallerAbort(opts: FetchBoardOptions): boolean {
  return opts.signal?.aborted === true;
}

/**
 * Fetch a generated, verified board for `topicRaw`. On success returns the BoardContent (already
 * Core-shaped). On ANY failure, falls back to a matching seed board if one exists; otherwise throws
 * a friendly BoardError. If the caller's signal aborts, the abort propagates (no fallback) so the
 * UI can cancel cleanly.
 */
export async function fetchBoard(
  topicRaw: string,
  opts: FetchBoardOptions = {},
): Promise<FetchBoardResult> {
  const trimmed = topicRaw.trim();
  let reason: BoardErrorReason = 'bad_response';

  try {
    const res = await postJson(`${apiBase()}/board`, { topic: trimmed }, opts);
    // 'accepted' only for useful answers (fresh board or 202 in-progress) — a 4xx/5xx heads straight
    // to the fallback path and must not flash a done checkmark first.
    if (res.ok || res.status === 202) opts.onPhase?.('accepted');
    if (res.status === 202) {
      // Cache-first: the verified board is generating in the BACKGROUND (the server returns 202 fast so
      // the player never waits ~50s), then caches it. POLL for the finished board so the player's ACTUAL
      // topic plays — rather than silently swapping in an unrelated default seed (the "Love Island ->
      // World Cup" bug: SEED_BOARDS[0] for any topic with no seed cousin). Only if it's still not ready
      // after the poll budget do we fall back, and then ONLY to a genuinely topic-matched seed, else an
      // honest "still cooking, try again" — never a wrong-topic board. Re-POSTs join the in-flight
      // server-side generation (no extra spend / no re-charge).
      return pollForGenerated(trimmed, opts);
    }
    if (!res.ok) {
      reason = reasonForStatus(res.status);
      return fallbackOrThrow(trimmed, reason);
    }
    const data: unknown = await res.json();
    const board = data as BoardContent;
    const check = validateBoardContent(board);
    if (!check.ok) {
      reason = 'bad_response';
      return fallbackOrThrow(trimmed, reason);
    }
    // Trust the backend's source/verified, but make sure the client treats it as generated content.
    opts.onPhase?.('verified');
    return { board: { ...board, source: 'generated' }, origin: 'generated' };
  } catch (err) {
    // A BoardError here is the no-seed-match throw from the !res.ok / bad-body branches above — it
    // already carries the correct reason; don't reclassify it as a network error.
    if (err instanceof BoardError) throw err;
    // A caller-initiated cancel must propagate, not silently become a seed board.
    if (isCallerAbort(opts)) throw err;
    reason = err instanceof DOMException && err.name === 'AbortError' ? 'timeout' : 'offline';
    return fallbackOrThrow(trimmed, reason);
  }
}

/** A cancellable delay that rejects (AbortError) if the caller's signal fires while waiting. */
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException('aborted', 'AbortError'));
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(new DOMException('aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Poll POST /board after an initial 202 until the backend has finished generating + caching the
 * VERIFIED board for `topicRaw` (returns 200), so the player's actual topic plays. Re-POSTs join the
 * in-flight server-side generation (no extra spend). On the caller's abort, the abort propagates. If
 * the board is still not ready within the poll budget: return a genuinely topic-matched seed if one
 * exists (labeled 'generating'), else throw a friendly 'generating' BoardError — NEVER a wrong-topic
 * board. This is the fix for a custom topic silently playing an unrelated default board.
 */
async function pollForGenerated(topicRaw: string, opts: FetchBoardOptions): Promise<FetchBoardResult> {
  const budget = opts.pollBudgetMs ?? 12_000;
  const interval = Math.max(250, opts.pollIntervalMs ?? 1_500);
  const deadline = Date.now() + budget;
  opts.onPhase?.('generating'); // 202: generating+verifying in the background; we poll
  let attempt = 0;
  while (budget > 0 && Date.now() < deadline) {
    if (opts.signal?.aborted) throw new DOMException('aborted', 'AbortError');
    attempt += 1;
    opts.onPoll?.(attempt);
    await delay(interval, opts.signal); // give the background gen time; propagates a real cancel
    let res: Response;
    try {
      res = await postJson(`${apiBase()}/board`, { topic: topicRaw }, opts);
    } catch (err) {
      if (isCallerAbort(opts)) throw err;
      continue; // transient network/timeout mid-poll — keep trying within the budget
    }
    if (res.status === 200) {
      const data: unknown = await res.json().catch(() => null);
      const board = data as BoardContent;
      if (board && validateBoardContent(board).ok) {
        opts.onPhase?.('verified');
        return { board: { ...board, source: 'generated' }, origin: 'generated' };
      }
      break; // 200 but malformed — stop polling, fall through to the honest fallback
    }
    if (res.status !== 202) break; // rate-limited / error — stop; honest fallback below
    // still 202 -> keep polling until the budget is exhausted
  }
  // Not ready in time: ONLY a genuinely on-topic seed, else an honest "still cooking" (no wrong board).
  const seed = findSeedFallback(topicRaw);
  if (seed) return { board: seed, origin: 'seed', fallbackReason: 'generating' };
  throw new BoardError('generating', friendlyFor('generating'));
}

/** Shared fallback path: matching seed board if any, else a friendly BoardError. */
function fallbackOrThrow(topicRaw: string, reason: BoardErrorReason): FetchBoardResult {
  const seed = findSeedFallback(topicRaw);
  if (seed) return { board: seed, origin: 'seed', fallbackReason: reason };
  throw new BoardError(reason, friendlyFor(reason));
}

/**
 * Fetch today's Daily board. On failure, falls back to a deterministic seed board (so Daily always
 * "works" offline) rather than throwing — the Daily button should never dead-end.
 */
export async function getDaily(opts: FetchBoardOptions = {}): Promise<FetchBoardResult> {
  try {
    const res = await getJson(`${apiBase()}/daily`, opts);
    opts.onPhase?.('accepted');
    if (!res.ok) return dailySeedFallback(reasonForStatus(res.status));
    const data = (await res.json()) as DailyResponse;
    const board = data?.board;
    const check = validateBoardContent(board);
    if (!check.ok) return dailySeedFallback('bad_response');
    opts.onPhase?.('verified');
    return { board: { ...board, source: 'generated' }, origin: 'generated' };
  } catch (err) {
    if (isCallerAbort(opts)) throw err;
    const reason: BoardErrorReason =
      err instanceof DOMException && err.name === 'AbortError' ? 'timeout' : 'offline';
    return dailySeedFallback(reason);
  }
}

/** Daily fallback: pick a seed board by the UTC day so a given day is stable offline. */
function dailySeedFallback(reason: BoardErrorReason): FetchBoardResult {
  const dayIndex = Math.floor(Date.now() / 86_400_000);
  const board = SEED_BOARDS[dayIndex % SEED_BOARDS.length];
  return { board, origin: 'seed', fallbackReason: reason };
}
